import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { InputNumber, Typography } from 'antd'
import { isEmpty, isNil, size } from 'lodash'
import PropTypes from 'prop-types'

import { useOutsideClick } from '../../../core/hooks'

import { StyledEditabledSelectContainer } from '../../../common/components/styled'
import { HOTKEYS_ENABLED_TAGS } from '../../../common/constants'

import { DISH_FIELDS_CONFIG, DISHES_AVAILABLE_HOTKEYS } from '../../constants'
import { formatDishPrice } from '../../helpers'
import { useDishEditableCellHotkey, useUpdateDishMutation } from '../../hooks'
import { dishPropType } from '../../prop-types'
import { transformDishToUpdateArgs } from '../../transformers'

const DishEditablePriceCell = ({ isDisabled, dish }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedValue, setSelectedValue] = useState([])
  const [updateDish] = useUpdateDishMutation(dish?.id)
  const [isSelectInFocus, setIsSelectInFocus] = useState(false)
  const [selectedInputIndex, setSelectedInputIndex] = useState(0)
  const inputRef = useRef([])

  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled, isFocusOnCell } =
    useDishEditableCellHotkey(dish.id, DISH_FIELDS_CONFIG.PRICE.value)

  const priceContainerRef = useRef(null)

  useEffect(() => {
    setSelectedValue([dish.lowPrice, dish.highPrice])
  }, [dish.highPrice, dish.id, dish.lowPrice])

  const handleClick = useCallback(
    e => {
      e.stopPropagation()
      if (!isDisabled) {
        setIsEditing(true)
      }
    },
    [isDisabled],
  )

  const handleSave = useCallback(() => {
    if (isSelectInFocus || !isEditing) {
      return
    }

    const lowPrice = selectedValue[0]
    const highPrice = selectedValue[1]

    updateDish(
      transformDishToUpdateArgs({
        lowPrice: isNil(lowPrice) && !isNil(highPrice) ? highPrice : lowPrice,
        highPrice: isNil(highPrice) && !isNil(lowPrice) ? lowPrice : highPrice,
      }),
    )

    setIsEditing(false)
    setHotkeyEditModeEnabled(false)
  }, [
    isEditing,
    isSelectInFocus,
    selectedValue,
    setHotkeyEditModeEnabled,
    updateDish,
  ])

  const handleBlur = useCallback(() => setIsSelectInFocus(false), [])
  const handleFocus = useCallback(() => setIsSelectInFocus(true), [])

  useEffect(() => {
    if (isSelectInHotkeyEditMode) {
      setIsEditing(isSelectInHotkeyEditMode)
    }
  }, [isSelectInHotkeyEditMode])

  useEffect(() => {
    if (isEditing && !isEmpty(inputRef.current)) {
      inputRef.current[0]?.focus()
      setSelectedInputIndex(0)
    }
  }, [isEditing])

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ESC.hotkey,
    handleSave,
    {
      enabled: isEditing,
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isEditing, handleSave],
  )

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.TAB.hotkey,
    event => {
      const newSelectIndex =
        selectedInputIndex < size(inputRef.current) - 1
          ? selectedInputIndex + 1
          : 0
      if (!isNil(inputRef.current[newSelectIndex])) {
        inputRef.current[newSelectIndex].focus()
        setSelectedInputIndex(newSelectIndex)
        event.preventDefault()
      }
    },
    {
      enabled: isFocusOnCell && isEditing,
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [selectedInputIndex, isFocusOnCell, isEditing],
  )

  const handleChangeLowPrice = useCallback(
    newValue => setSelectedValue([newValue, selectedValue[1]]),
    [selectedValue],
  )

  const handleChangeHighPrice = useCallback(
    newValue => setSelectedValue([selectedValue[0], newValue]),
    [selectedValue],
  )

  useOutsideClick(priceContainerRef, handleSave)

  return (
    <StyledEditabledSelectContainer
      onClick={handleClick}
      role="presentation"
      isDisabled={isDisabled}
      ref={priceContainerRef}
    >
      {isEditing && !isDisabled ? (
        <>
          <Typography.Text>Low price:</Typography.Text>

          <InputNumber
            ref={el => {
              inputRef.current[0] = el
            }}
            autoFocus
            value={selectedValue[0]}
            onChange={handleChangeLowPrice}
            placeholder="Low price"
            onBlur={handleBlur}
            onFocus={handleFocus}
            min={0}
            step={0.1}
          />
          <br />
          <Typography.Text>High price:</Typography.Text>
          <InputNumber
            ref={el => {
              inputRef.current[1] = el
            }}
            value={selectedValue[1]}
            onChange={handleChangeHighPrice}
            placeholder="High price"
            onBlur={handleBlur}
            onFocus={handleFocus}
            min={0}
            step={0.1}
          />
        </>
      ) : (
        formatDishPrice(dish)
      )}
    </StyledEditabledSelectContainer>
  )
}

DishEditablePriceCell.propTypes = {
  isDisabled: PropTypes.bool,
  dish: dishPropType.isRequired,
}

DishEditablePriceCell.defaultProps = {
  isDisabled: false,
}

export default DishEditablePriceCell
